import { ALG, PAT, SHX } from "@/constants/testIds";
import {
  ALLERGIES_EMPTY,
  ALLERGIES_NKA,
  MEDICAL_HISTORY_EMPTY,
  PROBLEM_LIST_EMPTY,
  SURGICAL_HISTORY_EMPTY,
  formatSummaryHistory,
  formatSummaryProblem,
} from "@/lib/patientSummary";

function Card({ title, action, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="emr-chart-card p-4"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function ViewAll({ onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="text-xs font-medium text-[#1E79E8] hover:text-[#1E79E8] hover:underline"
    >
      View All
    </button>
  );
}

function DiagnosisRows({ items, testIdPrefix }) {
  return (
    <ul className="space-y-2 text-sm">
      {items.map((item, index) => {
        const row = formatSummaryProblem(item);
        const key = row.code || item.id || `${row.description}-${index}`;
        return (
          <li
            key={key}
            data-testid={`${testIdPrefix}-${row.code || index}`}
            className="flex items-start justify-between gap-3"
          >
            {row.code ? (
              <span className="shrink-0 font-fhir text-xs text-slate-500">{row.code}</span>
            ) : null}
            <span className="min-w-0 flex-1 text-slate-800">{row.description}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function ProblemListCard({ items = [], onViewAll }) {
  return (
    <Card
      title="Problem List"
      testId={PAT.chartCardProblemList}
      action={<ViewAll onClick={onViewAll} testId={PAT.chartViewAllProblems} />}
    >
      {items.length ? (
        <DiagnosisRows items={items} testIdPrefix={PAT.chartProblemRow} />
      ) : (
        <p className="text-sm text-slate-400">{PROBLEM_LIST_EMPTY}</p>
      )}
    </Card>
  );
}

export function MedicalHistoryCard({ conditions = [], negatives = [], onViewAll }) {
  const empty = conditions.length === 0 && negatives.length === 0;
  return (
    <Card
      title="Medical History"
      testId={PAT.chartCardMedicalHistory}
      action={<ViewAll onClick={onViewAll} testId={PAT.chartViewAllHistory} />}
    >
      {empty ? (
        <p className="text-sm text-slate-400">{MEDICAL_HISTORY_EMPTY}</p>
      ) : (
        <div className="space-y-3">
          {conditions.length ? (
            <DiagnosisRows
              items={conditions.map((item) => formatSummaryHistory(item))}
              testIdPrefix={PAT.chartHistoryRow}
            />
          ) : null}
          {negatives.length ? (
            <div>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Pertinent Negatives
              </h3>
              <ul className="space-y-1 text-sm text-slate-700">
                {negatives.map((item, index) => (
                  <li
                    key={item.id || item.description || index}
                    data-testid={`${PAT.chartHistoryNegative}-${index}`}
                  >
                    {item.description}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

export function AllergiesCard({ items = [], noKnownAllergies = false, onViewAll }) {
  return (
    <Card
      title="Allergies"
      testId={ALG.card}
      action={<ViewAll onClick={onViewAll} testId={ALG.viewAll} />}
    >
      {items.length ? (
        <ul className="space-y-2 text-sm">
          {items.map((item, index) => (
            <li
              key={item.id || item.allergenId || index}
              className="flex items-start justify-between gap-3"
            >
              <span className="min-w-0 flex-1 text-slate-800">{item.allergenDisplayName}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">{noKnownAllergies ? ALLERGIES_NKA : ALLERGIES_EMPTY}</p>
      )}
    </Card>
  );
}

export function SurgicalHistoryCard({ items = [], onViewAll }) {
  return (
    <Card
      title="Surgical History"
      testId={SHX.card}
      action={<ViewAll onClick={onViewAll} testId={SHX.viewAll} />}
    >
      {items.length ? (
        <ul className="space-y-2 text-sm">
          {items.map((item, index) => (
            <li
              key={item.id || item.localCode || index}
              className="flex items-start justify-between gap-3"
            >
              <span className="shrink-0 font-fhir text-xs text-slate-500">{item.localCode}</span>
              <span className="min-w-0 flex-1 text-slate-800">{item.display}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">{SURGICAL_HISTORY_EMPTY}</p>
      )}
    </Card>
  );
}
